import React, { useState } from 'react'
import { Field, Form, Formik } from 'formik'
import { string, object } from 'yup'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import { TextField } from 'formik-material-ui'

import Link from '../General/Link'
import GoogleButton from '../Brands/GoogleButton'
import ErrorMessages from '../../helpers/ErrorMessages'
import useRecaptcha from '../Hooks/useRecaptcha'
import { Box, Container, makeStyles } from '@material-ui/core'
import { ArrowRightAlt } from '@material-ui/icons'
import { Arrow } from '../../../public/images/icons/arrow'

const ValidationSchema = object().shape({
    first_name: string()
        .required(ErrorMessages.required)
        .min(3, ErrorMessages.min)
        .max(30, ErrorMessages.max),
    last_name: string()
        .required(ErrorMessages.required)
        .min(3, ErrorMessages.min)
        .max(30, ErrorMessages.max),
    email: string()
        .required(ErrorMessages.required)
        .email(ErrorMessages.email)
        .max(50, ErrorMessages.max),
    phone: string().min(5, ErrorMessages.min).max(20, ErrorMessages.max),
    password: string()
        .required(ErrorMessages.required)
        .min(6, ErrorMessages.min)
        .max(30, ErrorMessages.max),
})

const useStyles = makeStyles((theme) => ({
    form: {
        background: '#F2F2F2',
        padding: theme.spacing(14, 20, 9),
        height: 'fit-content',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            padding: theme.spacing(0, 2),
            height: 'fit-content',
        },
    },
    input: {
        height: '49px',
        '& .MuiInputBase-input': {
            backgroundColor: '#F2F2F2 !important',
            WebkitBoxShadow: '0 0 0 30px #F2F2F2 inset !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            backgroundColor: '#F2F2F2',
        },
        '& .MuiInputLabel-filled ': {
            color: theme.palette.grey.light,
            fontFamily: " 'Inter', serif",
            fontSize: '17px',
            lineHeight: '29px',
            background: 'transprent',
            fontWeight: '300',
        },
        '& .MuiInputBase-root ': {
            color: theme.palette.grey.light,
            fontFamily: " 'Inter', serif",
            fontSize: '17px',
            lineHeight: '29px',
            background: 'transprent',
            fontWeight: '300',
        },
    },
    passwordInput: {
        '& .MuiInputBase-input': {
            backgroundColor: '#F2F2F2 !important',
            WebkitBoxShadow: '0 0 0 30px #F2F2F2 inset !important',
        },
    },
    header: {
        marginBottom: theme.spacing(5),
        [theme.breakpoints.down('md')]: {
            marginBottom: theme.spacing(0),
        },
    },
    btnBox: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(5),
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '20px',
        [theme.breakpoints.down('md')]: {
            marginTop: theme.spacing(2),
        },
    },
    signupbtn: {
        height: '68px',
        width: '215px',
        color: '#fff',
        borderRadius: '0px',
        border: '1px solid #c09167',
        '&:hover': {
            background: theme.palette.primary.main,
        },
    },
    signinbtn: {
        border: '1.5px solid',
        borderColor: theme.palette.primary.light,
        height: '68px',
        width: '215px',
        color: '#fff',
        color: theme.palette.secondary.main,
        borderRadius: '0px',
        '&:hover': {
            background: theme.palette.primary.main,
            '& .MuiButton-label': {
                color: '#fff',
            },
        },
    },
    policyText: {
        color: theme.palette.grey.light,
        textAlign: 'center',
        fontSize: '14px',
    },
}))

const RegisterForm = ({ onGooglePressed, ...props }) => {
    const classes = useStyles()
    const recaptcha = useRecaptcha(props.onSubmit)
    const [isHoverd, setIsHoverd] = useState(false)
    const onSubmit = (values, actions) =>
        recaptcha(
            { ...values, password_confirmation: values.password },
            actions
        )

    return (
        <Container className={classes.form}>
            <Formik
                initialValues={{
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    password: '',
                }}
                onSubmit={onSubmit}
                validationSchema={ValidationSchema}
            >
                {({ isSubmitting, handleSubmit }) => (
                    <Form>
                        <Grid container spacing={6}>
                            <Grid item xs={12} md={12}>
                                <Typography
                                    variant="h2"
                                    color="secondary"
                                    align="center"
                                    className={classes.header}
                                >
                                    Register to Bayshire Realty
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field
                                    component={TextField}
                                    name="first_name"
                                    id="first_name"
                                    label="First Name"
                                    fullWidth
                                    variant="filled"
                                    className={classes.input}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field
                                    component={TextField}
                                    name="last_name"
                                    id="last_name"
                                    label="Last Name"
                                    fullWidth
                                    variant="filled"
                                    className={classes.input}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field
                                    component={TextField}
                                    name="phone"
                                    id="phone"
                                    label="Phone Number"
                                    type="tel"
                                    fullWidth
                                    variant="filled"
                                    className={classes.input}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Field
                                    component={TextField}
                                    name="email"
                                    id="email"
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    variant="filled"
                                    className={classes.input}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <Field
                                    component={TextField}
                                    name="password"
                                    id="password"
                                    label="Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    className={classes.passwordInput}
                                    InputLabelProps={{
                                        className: props.className,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography
                                    variant="body1"
                                    className={classes.policyText}
                                >
                                    By continuing, you accept Rasti Team{' '}
                                    <Link
                                        muiProps={{ underline: 'always' }}
                                        href={'/legal/terms-of-use'}
                                    >
                                        Terms of Use
                                    </Link>{' '}
                                    and{' '}
                                    <Link
                                        muiProps={{ underline: 'always' }}
                                        href={'/legal/privacy-policy'}
                                    >
                                        Privacy Policy
                                    </Link>
                                    .
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                container
                                justifyContent="center"
                            >
                                {isSubmitting ? (
                                    <CircularProgress color="primary" />
                                ) : (
                                    <Box className={classes.btnBox}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            onClick={handleSubmit}
                                            className={classes.signupbtn}
                                        >
                                            Sign Up
                                            <Arrow color={'#fff'} />
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            type="submit"
                                            onClick={handleSubmit}
                                            className={classes.signinbtn}
                                            onMouseOver={() => {
                                                setIsHoverd(true)
                                            }}
                                            onMouseLeave={() =>
                                                setIsHoverd(false)
                                            }
                                        >
                                            Sign In{' '}
                                            <Arrow
                                                color={
                                                    isHoverd
                                                        ? '#fff'
                                                        : '#003466'
                                                }
                                            />
                                        </Button>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Container>
    )
}

export default RegisterForm
